import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import { ArrowBack, Memory } from "@mui/icons-material";
import { useAgent } from "@/hooks/useAgents";
import apiClient from "@/api/client";
import { brand } from "@/theme";

interface MemoryStats {
  active: number;
  archived: number;
  total: number;
  by_type: Array<{
    memory_type: string;
    count: number;
    avg_importance: number;
  }>;
  top_accessed: Array<{
    id: string;
    content: string;
    access_count: number;
    importance: number;
  }>;
  recently_archived: Array<{
    id: string;
    content: string;
    importance: number;
  }>;
  pending_consolidation: number;
}

const TYPE_COLORS: Record<string, string> = {
  learned: brand.forestGreen,
  context: "#1565C0",
  preference: brand.warmGold,
  goal_update: "#6A1B9A",
};

export default function MemoryViewer() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { data: agent } = useAgent(agentId);

  const {
    data: stats,
    isLoading,
    error,
  } = useQuery<MemoryStats>({
    queryKey: ["memory-stats", agentId],
    queryFn: async () => {
      const res = await apiClient.get<MemoryStats>(`/memory/${agentId}`);
      return res.data;
    },
    enabled: !!agentId,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load memory data.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/agents/${agentId}`)}>
          Back
        </Button>
        <Memory color="action" />
        <Typography variant="h4">
          {agent?.code} {agent?.name} — Memory Health
        </Typography>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 3 }}>
        {[
          { label: "Active", value: stats?.active ?? 0, color: brand.forestGreen },
          { label: "Archived", value: stats?.archived ?? 0, color: "#999" },
          { label: "Total", value: stats?.total ?? 0, color: brand.nearBlack },
          {
            label: "Pending Consolidation",
            value: stats?.pending_consolidation ?? 0,
            color: brand.warmGold,
          },
        ].map((card) => (
          <Card key={card.label}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h3" sx={{ color: card.color, fontWeight: 600 }}>
                {card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {/* By type */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              By Type
            </Typography>
            {stats?.by_type && stats.by_type.length > 0 ? (
              stats.by_type.map((t) => {
                const maxCount = Math.max(...stats.by_type.map((x) => x.count));
                return (
                  <Box key={t.memory_type} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ textTransform: "capitalize" }}
                      >
                        {t.memory_type.replace("_", " ")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.count} memories · avg importance: {t.avg_importance}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(t.count / maxCount) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: "#eee",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: TYPE_COLORS[t.memory_type] ?? brand.forestGreen,
                        },
                      }}
                    />
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                No memories recorded yet.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Top accessed */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Most Accessed
            </Typography>
            {stats?.top_accessed && stats.top_accessed.length > 0 ? (
              stats.top_accessed.map((m, i) => (
                <Box
                  key={m.id}
                  sx={{
                    mb: 1,
                    pb: 1,
                    borderBottom: i < stats.top_accessed.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2">&ldquo;{m.content}&rdquo;</Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Chip label={`${m.access_count} accesses`} size="small" variant="outlined" />
                    <Chip label={`importance: ${m.importance}`} size="small" variant="outlined" />
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No memories accessed yet.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Recently archived */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recently Archived
            </Typography>
            {stats?.recently_archived && stats.recently_archived.length > 0 ? (
              stats.recently_archived.map((m) => (
                <Box key={m.id} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    &ldquo;{m.content}&rdquo;
                  </Typography>
                  <Chip label={`importance: ${m.importance}`} size="small" sx={{ mt: 0.5 }} />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No archived memories.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
